<script setup>
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import AudioAlertTone from './AudioAlertTone.vue';
import AudioAlertEvent from './AudioAlertEvent.vue';
import AudioAlertCondition from './AudioAlertCondition.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
const store = useStore();
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { initializeAudioAlerts } from 'dashboard/helper/scriptHelpers';
import { useStoreGetters } from 'dashboard/composables/store';
import {
  DEFAULT_AUDIO_ALERT_EVENTS,
  DEFAULT_AUDIO_ALERT_TONE,
} from './constants';
import {
  buildTestNotification,
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
  showDesktopNotification,
} from 'dashboard/helper/AudioAlerts/DesktopNotificationHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const currentUser = computed(() => getters.getCurrentUser.value);

const { uiSettings, updateUISettings } = useUISettings();

const { t } = useI18n();
const audioAlert = ref('');
const playAudioWhenTabIsInactive = ref(false);
const alertIfUnreadConversationExist = ref(false);
const desktopPopupEnabled = ref(true);
const desktopPopupPermission = ref(getDesktopNotificationPermission());
const alertTone = ref(DEFAULT_AUDIO_ALERT_TONE);
const audioAlertConditions = ref([]);
const i18nKeyPrefix = 'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION';

const initializeNotificationUISettings = newUISettings => {
  const updatedUISettings = camelcaseKeys(newUISettings);

  audioAlert.value =
    updatedUISettings.enableAudioAlerts || DEFAULT_AUDIO_ALERT_EVENTS;
  playAudioWhenTabIsInactive.value = !updatedUISettings.alwaysPlayAudioAlert;
  alertIfUnreadConversationExist.value =
    updatedUISettings.alertIfUnreadAssignedConversationExist;
  desktopPopupEnabled.value = updatedUISettings.enableDesktopPopup !== false;
  audioAlertConditions.value = [
    {
      id: 'audio1',
      label: t(`${i18nKeyPrefix}.CONDITIONS.CONDITION_ONE`),
      model: playAudioWhenTabIsInactive.value,
      value: 'tab_is_inactive',
    },
    {
      id: 'audio2',
      label: t(`${i18nKeyPrefix}.CONDITIONS.CONDITION_TWO`),
      model: alertIfUnreadConversationExist.value,
      value: 'conversations_are_read',
    },
    {
      id: 'audio3',
      label: t(`${i18nKeyPrefix}.CONDITIONS.CONDITION_THREE`),
      model: desktopPopupEnabled.value,
      value: 'desktop_popup',
    },
  ];
  alertTone.value =
    updatedUISettings.notificationTone || DEFAULT_AUDIO_ALERT_TONE;
};

watch(
  uiSettings,
  value => {
    initializeNotificationUISettings(value);
  },
  { immediate: true }
);

const handleAudioConfigChange = value => {
  updateUISettings(value);
  initializeAudioAlerts(currentUser.value);
  useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
};

onMounted(() => {
  store.dispatch('userNotificationSettings/get');
});

const handAudioAlertChange = value => {
  audioAlert.value = value;
  handleAudioConfigChange({
    enable_audio_alerts: value,
  });
};
const handleAudioAlertConditions = async (id, value) => {
  if (id === 'tab_is_inactive') {
    handleAudioConfigChange({
      always_play_audio_alert: !value,
    });
  } else if (id === 'conversations_are_read') {
    handleAudioConfigChange({
      alert_if_unread_assigned_conversation_exist: value,
    });
  } else if (id === 'desktop_popup') {
    handleAudioConfigChange({ enable_desktop_popup: value });
    if (value && desktopPopupPermission.value === 'default') {
      desktopPopupPermission.value =
        await requestDesktopNotificationPermission();
    }
  }
};
const handleAudioToneChange = value => {
  handleAudioConfigChange({ notification_tone: value });
};

// Lets an agent check that their computer actually shows pop-ups: the OS can
// block the browser's notifications even when the site permission is granted.
const sendTestPopup = async () => {
  if (desktopPopupPermission.value === 'default') {
    desktopPopupPermission.value = await requestDesktopNotificationPermission();
  }
  if (desktopPopupPermission.value !== 'granted') return;

  showDesktopNotification(
    buildTestNotification({
      title: t(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.TEST_TITLE'
      ),
      body: t(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.TEST_BODY'
      ),
    }),
    () => window.focus()
  );
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <AudioAlertTone
      :value="alertTone"
      :label="$t(`${i18nKeyPrefix}.DEFAULT_TONE.TITLE`)"
      @change="handleAudioToneChange"
    />

    <AudioAlertEvent
      :label="$t(`${i18nKeyPrefix}.ALERT_TYPE.TITLE`)"
      :value="audioAlert"
      @update="handAudioAlertChange"
    />

    <AudioAlertCondition
      :items="audioAlertConditions"
      :label="$t(`${i18nKeyPrefix}.CONDITIONS.TITLE`)"
      @change="handleAudioAlertConditions"
    />

    <p
      v-if="desktopPopupEnabled && desktopPopupPermission === 'denied'"
      class="text-body-main text-n-ruby-11"
    >
      {{ $t(`${i18nKeyPrefix}.DESKTOP_POPUP.BLOCKED`) }}
    </p>

    <div
      v-if="desktopPopupEnabled && desktopPopupPermission !== 'unsupported'"
      class="flex flex-col items-start gap-2"
    >
      <NextButton
        :label="
          $t(
            'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.TEST_BUTTON'
          )
        "
        icon="i-lucide-bell-ring"
        color="slate"
        variant="faded"
        size="sm"
        :disabled="desktopPopupPermission === 'denied'"
        @click="sendTestPopup"
      />
      <p class="text-body-main text-n-slate-11">
        {{
          $t(
            'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.DESKTOP_POPUP.TEST_HELP'
          )
        }}
      </p>
    </div>
  </div>
</template>
